import router from './router'
import store from './store'
import storage from 'store'
import NProgress from 'nprogress' // progress bar
import '@/components/NProgress/nprogress.less' // progress bar custom style
import notification from 'ant-design-vue/es/notification'
import Modal from 'ant-design-vue/es/modal'
import { setDocumentTitle, domTitle } from '@/utils/domUtil'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import { i18nRender } from '@/locales'
import {
  FALLBACK_ROUTE_PATH,
  isPathInRoutes,
  resolveFallbackRoutePath
} from '@/utils/routeAccess'

NProgress.configure({ showSpinner: false }) // NProgress Configuration

const allowList = ['login', 'register', '/callback', '/auth-redirect', '/bind'] // no redirect allowList
const loginRoutePath = '/user/login'
const getDefaultRoutePath = () => store.getters.defaultRoutePath || ''

const isHomePath = (path) => path === '/index' || path === '/' || path === ''

const getAccessibleRoutes = () => store.getters.routers || []

const isRouteAccessible = (path) => {
  if (!path || path === '/') {
    return false
  }
  return isPathInRoutes(path, getAccessibleRoutes())
}

const pickFallbackPath = () => {
  const routes = getAccessibleRoutes()
  const defaultPath = getDefaultRoutePath()
  if (defaultPath && isPathInRoutes(defaultPath, routes)) {
    return defaultPath
  }
  return resolveFallbackRoutePath(routes, FALLBACK_ROUTE_PATH) || ''
}

const navigateToDefault = (next, to, replace = true) => {
  const targetPath = pickFallbackPath()
  if (!targetPath) {
    next({ path: loginRoutePath, replace })
    return
  }
  next({ path: targetPath, replace })
}

/** 无菜单权限：先跳登录避免白屏，再弹窗提示 */
const handleNoMenuPermission = (next) => {
  // 必须立刻结束当前导航，否则守卫挂起白屏
  next({ path: loginRoutePath, replace: true })
  NProgress.done()
  const showTip = () => {
    Modal.warning({
      title: '提示',
      content: '当前用户没有菜单权限',
      okText: '确定',
      zIndex: 2000,
      onOk: () => {
        if (router.currentRoute.path !== loginRoutePath) {
          router.replace(loginRoutePath).catch(() => {})
        }
      }
    })
  }
  store.dispatch('Logout', { remote: false }).then(showTip).catch(showTip)
}

const loadRoutesAndContinue = (next, to) => {
  return store.dispatch('GenerateRoutes').then(() => {
    router.addRoutes(store.getters.routers)
    if (isHomePath(to.path) || to.path === loginRoutePath) {
      navigateToDefault(next, to)
    } else if (!isRouteAccessible(to.path)) {
      // 目标页不在当前角色菜单内，避免 next(to) 匹配不到路由导致白屏
      navigateToDefault(next, to)
    } else {
      next({ ...to, replace: true })
    }
  })
}

router.beforeEach((to, from, next) => {
  NProgress.start() // start progress bar
  to.meta && (typeof to.meta.title !== 'undefined' && setDocumentTitle(`${i18nRender(to.meta.title)} - ${domTitle}`))
  /* has token */
  if (storage.get(ACCESS_TOKEN)) {
    // 已在登录页：有 token 也直接放行，避免过期后残留 defaultPath 又把人踢回首页造成白屏循环
    if (to.path === loginRoutePath) {
      next()
      NProgress.done()
      return
    }
    const needLoadRoutes = store.getters.roles.length === 0 || !getDefaultRoutePath()
    if (needLoadRoutes) {
      const afterInfo = () => {
        loadRoutesAndContinue(next, to).catch((err) => {
          if (err && err.code === 'NO_MENU_PERMISSION') {
            handleNoMenuPermission(next)
            return
          }
          notification.error({
            message: '错误',
            description: (err && err.message) || '加载菜单失败，请重试'
          })
          next({ path: loginRoutePath, replace: true })
          NProgress.done()
          store.dispatch('Logout', { remote: false })
        })
        store.dispatch('getCity')
      }
      if (store.getters.roles.length === 0) {
        store.dispatch('GetInfo').then(afterInfo).catch(() => {
          // 必须立刻 next，否则守卫挂起白屏；先跳登录再清本地，避免被 Logout 微任务拖住
          next({ path: loginRoutePath, replace: true })
          NProgress.done()
          store.dispatch('Logout', { remote: false })
        })
      } else {
        afterInfo()
      }
      return
    }
    if (isHomePath(to.path)) {
      navigateToDefault(next, to)
      NProgress.done()
      return
    }
    if (!isRouteAccessible(to.path)) {
      navigateToDefault(next, to)
      NProgress.done()
      return
    }
    next()
  } else {
    if (allowList.includes(to.name)) {
      // 在免登录名单，直接进入
      next()
    } else {
      next({ path: loginRoutePath, query: { redirect: to.fullPath } })
      NProgress.done && NProgress.done() // if current page is login will not trigger afterEach hook, so manually handle it
    }
  }
})

router.afterEach(() => {
  NProgress.done() // finish progress bar
})
